// src/components/DataTable.jsx
import React from 'react';
import { Download, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import Select from 'react-select';

const DataTable = ({ 
  currentData, 
  rowsPerPage, 
  onRowsPerPageChange, 
  timeRange, 
  onTimeRangeChange, 
  onExportCSV, 
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  selectedSensors,
  onSensorChange,
  showForecast,
  onToggleForecast
}) => {
  const sensorOptions = [
    { value: 'temperature', label: 'Temperature', color: '#8884d8' },
    { value: 'relative_humidity', label: 'Humidity', color: '#82ca9d' },
    { value: 'pressure', label: 'Pressure', color: '#ffc658' }
  ];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      height: '38px'
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '38px',
      padding: '0 6px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px'
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '38px'
    })
  };

  const getUnit = (sensorType) => {
    switch (sensorType) {
      case 'temperature':
        return '°C';
      case 'relative_humidity':
        return '%';
      case 'pressure':
        return 'hPa';
      default:
        return '';
    }
  };

  const formatValue = (value, sensorType) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(2)} ${getUnit(sensorType)}`;
  };

  const columns = [
    { header: 'Timestamp', key: 'timestamp' },
    ...selectedSensors.map(sensor => ({
      header: sensor.label,
      key: sensor.value
    }))
  ];

  // Debug log to check incoming data
  console.log('Current data in DataTable:', currentData);

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <div className="flex items-end gap-4">
          <div className="w-96">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Sensors to Display
            </label>
            <Select
              isMulti
              value={selectedSensors}
              onChange={onSensorChange}
              options={sensorOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              defaultValue={sensorOptions}
              styles={customSelectStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Range
            </label>
            <select
              value={timeRange}
              onChange={onTimeRangeChange}
              className="border rounded p-2 bg-white text-gray-700"
            >
              <option value="1h">Past Hour</option>
              <option value="1d">Past Day</option>
              <option value="3d">Past 3 Days</option>
            </select>
          </div>
        </div>
        <button
          onClick={onExportCSV}
          className="flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200"
          title="Download as CSV"
        >
          <Download size={20} />
        </button>
      </div>

      <div className="overflow-x-auto shadow-md rounded-lg">
        <table className="w-full border-collapse bg-white">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="p-3 text-left border-b"
                >
                  <div 
                    className="flex items-center cursor-pointer"
                    onClick={() => onSort(column.key)}
                  >
                    <span>{column.header}</span>
                    <ArrowUpDown size={16} className="ml-1" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.isArray(currentData) && currentData.map((row, rowIndex) => (
              <tr 
                key={rowIndex}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="p-3 border-b">
                  {new Date(row.timestamp).toLocaleString()}
                </td>
                {selectedSensors.map(sensor => (
                  <td 
                    key={sensor.value} 
                    className="p-3 border-b text-left"
                  >
                    {row[sensor.value] != null ? 
                      `${row[sensor.value].toFixed(2)} ${getUnit(sensor.value)}` 
                      : 'N/A'}
                  </td>
                ))}
              </tr>
            ))}
            {(!Array.isArray(currentData) || currentData.length === 0) && (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="p-3 text-center text-gray-500"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="p-2 border rounded disabled:opacity-50 bg-white text-green-700 hover:bg-green-50 disabled:hover:bg-white"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 border rounded disabled:opacity-50 bg-white text-green-700 hover:bg-green-50 disabled:hover:bg-white"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded disabled:opacity-50 bg-white text-green-700 hover:bg-green-50 disabled:hover:bg-white"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded disabled:opacity-50 bg-white text-green-700 hover:bg-green-50 disabled:hover:bg-white"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
            className="border rounded p-2 bg-white text-gray-700"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default DataTable;